import React, {
  useState,
  useEffect,
  useRef,
  createContext,
  useContext,
} from 'react';
import axios from 'axios';
import { unitedStatesLocale, canadianLocale } from '@data/locales';
import { useLocale } from '@util/providers/Locale';
import { useStateWithSessionStorage } from '@util/hooks/useStateWithSessionStorage';
import { useStateWithLocalStorage } from '@util/hooks/useStateWithLocalStorage';
import { getCountryCodeFromCountryName } from '@util/addressFunctions';
import URLS from '@config/urls';
import {
  mgr,
  iframeSignin,
  startSigninMainWindow,
  startSignoutMainWindow,
  onCrossTabSignOut,
} from '@config/oidc-config';
import i18n from '../../i18n/i18n';
import navigationWrapper from '../navigationWrapper';

/* eslint-disable */
class SsoUser {
  constructor(access_token, name, expires_at, given_name) {
    this.access_token = access_token;
    this.name = name;
    this.expires_at = expires_at;
    this.given_name = given_name;
  }
}

class BmoUser {
  constructor(
    firstName,
    lastName,
    email,
    accountType,
    subscriptionInfo,
    zuoraInfo,
    PONumber,
    studioLicenses
  ) {
    this.firstName = firstName;
    this.lastName = lastName;
    this.email = email;
    this.accountType = accountType;
    this.subscriptionInfo = subscriptionInfo;
    this.zuoraInfo = zuoraInfo;
    this.PONumber = PONumber;
    this.studioLicenses = studioLicenses;
  }
}

const UserContext = createContext({});

export const loadingStatus = {
  LOG_IN_SSO: 'Logging in with Tobii Dynavox SSO account',
  GET_SSO: 'Checking for Tobii Dynavox SSO account',
  GET_BMO: 'Checking for Boardmaker user',
  GET_BMO_SUB: 'Getting subscription information',
  GET_FUTURE_AMENDMENTS: 'Getting Pending Modifications',
  GET_LICENSES: 'Getting Licenses',
  DONE: 'DONE!',
};

export default function AuthProvider({ children }) {
  const [status, setStatus] = useState();
  const [ssoUser, setSsoUser] = useState();
  const [bmoUser, setBmoUser] = useState();
  const [signedOutElsewhere, setSignedOutElsewhere] = useState(false);
  const [shouldGetBmoUser, setShouldGetBmoUser] = useState(false);
  //* tracks the access token we've already attempted a BMO-user lookup for, so
  //* repeated ssoUser-effect runs (e.g. from automaticSilentRenew rotating the
  //* user object) don't re-fire /me and wedge status back at GET_BMO.
  const bmoFetchedForToken = useRef(null);
  const [hasGeoRedirected, setHasGeoRedirected] = useStateWithSessionStorage(
    'hasGeoRedirected',
    false,
    false
  );
  const [hasManualLocaleSelection] = useStateWithLocalStorage(
    'hasManualLocaleSelection',
    false,
    false
  );
  const [hasManualLanguageSelection] = useStateWithLocalStorage(
    'hasManualLanguageSelection',
    false,
    false
  );

  const {
    selectedCurrency,
    setSelectedCurrency,
    setLocale,
    selectedLanguage,
    setSelectedLanguage,
    locale,
  } = useLocale();

  // change language when the selected language changes
  useEffect(() => {
    if (selectedLanguage?.code) i18n.changeLanguage(selectedLanguage.code);
  }, [selectedLanguage]);

  // redirect only when geo/location or bmoUser changes
  useEffect(() => {
    redirectByGeolocation();
  }, [locale, bmoUser]);

  useEffect(() => {
    const accountCurrency =
      bmoUser?.zuoraInfo?.basicInfo?.currency ?? bmoUser?.zuoraAccountCurrency;
    if (!accountCurrency) return;
    const isCanadaRegion = locale?.localeDetails?.isoCode === 'CA';
    if (accountCurrency === 'CAD') {
      if (!isCanadaRegion) setLocale(canadianLocale);
      if (selectedCurrency !== 'CAD') setSelectedCurrency('CAD');
    } else if (isCanadaRegion) {
      setLocale(unitedStatesLocale);
    }
  }, [
    locale,
    selectedCurrency,
    bmoUser?.zuoraInfo,
    bmoUser?.zuoraAccountCurrency,
  ]);

  //* attempts silent sign in
  //* triggered by: full page refresh, post create account log in
  async function signInSilent({ getBmoUser = true, geoLocate = true } = {}) {
    getBmoUser
      ? setStatus(loadingStatus.GET_SSO)
      : setStatus(loadingStatus.LOG_IN_SSO);
    setShouldGetBmoUser(getBmoUser);
    !bmoUser && geoLocate && getGeoLocation();
    const user = await iframeSignin();
    //* update user from undefined to null to show that there was an attempt to log in silently, but the user was not already logged in
    !user && addSsoUser(null);
  }

  //* sign in
  //* triggered by: user interaction with log in button
  //* returnPath defaults to the current page
  function signIn(returnPath) {
    setStatus(loadingStatus.LOG_IN_SSO);
    startSigninMainWindow(returnPath);
  }

  //* sign out
  function signOut() {
    startSignoutMainWindow();
  }

  //* add sso user
  function addSsoUser(user) {
    setSsoUser(
      user
        ? new SsoUser(
            user.access_token,
            user.profile.name,
            user.expires_at,
            user.profile.given_name
          )
        : null
    );
  }

  //* on mount
  useEffect(() => {
    //* use a cached user when available to avoid burning a refresh token on every mount;
    //* automaticSilentRenew handles renewal as the access token approaches expiry.
    //* when no user is stored at all the user may still have a live SSO cookie at the
    //* authority (e.g. arrived from Boardmaker Online) — try the iframe silent flow once
    //* per tab so they get auto-logged-in without clicking Login. The sessionStorage flag
    //* prevents repeating the iframe round-trip on every reload after we've concluded
    //* there's no SSO session.
    mgr.getUser().then((user) => {
      if (user && !user.expired) {
        setShouldGetBmoUser(true);
        addSsoUser(user);
        !bmoUser && getGeoLocation();
      } else if (user) {
        signInSilent();
      } else if (!sessionStorage.getItem('silentSigninAttempted')) {
        sessionStorage.setItem('silentSigninAttempted', '1');
        signInSilent();
      } else {
        addSsoUser(null);
        getGeoLocation();
      }
    });

    //* runs when a user is loaded either by user interaction or silent sign in
    const onUserLoaded = (user) => addSsoUser(user || null);

    //* runs when the stored user is removed (e.g. renewal failed after the SSO
    //* session ended elsewhere) so the UI updates without a page reload
    const onUserUnloaded = () => {
      setBmoUser(undefined);
      addSsoUser(null);
    };

    mgr.events.addUserLoaded(onUserLoaded);
    mgr.events.addUserUnloaded(onUserUnloaded);

    //* runs only when THIS tab was signed out by another tab, not for any
    //* other removeUser() cause, so we can tell the user why they landed
    //* back in a logged-out state instead of leaving it unexplained
    const unsubscribeCrossTabSignOut = onCrossTabSignOut(() => {
      setSignedOutElsewhere(true);
    });

    return () => {
      mgr.events.removeUserLoaded?.(onUserLoaded);
      mgr.events.removeUserUnloaded?.(onUserUnloaded);
      unsubscribeCrossTabSignOut();
    };
    // eslint-disable-next-line
  }, []);

  function dismissSignedOutElsewhere() {
    setSignedOutElsewhere(false);
  }

  //* get bmo user information
  async function getBmoUser() {
    //* check for bmo user
    setStatus(loadingStatus.GET_BMO);
    await axios
      .get(URLS.CREATE_BMO_ACCOUNT, {
        headers: {
          Authorization: `Bearer ${ssoUser.access_token}`,
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
          Expires: 'Sat, 01 Jan 2000 00:00:00 GMT',
        },
      })
      .then((res) => {
        const user = res.data;
        setBmoUser(
          new BmoUser(
            user.firstName,
            user.lastName,
            user.email,
            user.accountType,
            user.subscriptionInfo
          )
        );

        if (
          user.accountType === 'District' ||
          (user.accountType === 'Free' &&
            user.subscriptionInfo?.planType === 'District')
        ) {
          navigationWrapper('/downloads');
          if (window.location.hash.toLowerCase() === '#studentcenter') {
            navigationWrapper('/downloads/#studentcenter');
          }
        }
        if (user.subscriptionInfo) {
          getZuoraInfo();
          getLicenses(user.id);
        } else if (user.zuoraAccountId) {
          getZuoraAccountCurrency();
        } else {
          //* done
          setStatus(loadingStatus.DONE);
        }
      })
      .catch((err) => {
        //* done
        setStatus(loadingStatus.DONE);
        redirectByGeolocation();
      });
  }

  async function setLocaleFromBillingCountry(billingCountry, accountCurrency) {
    if (!billingCountry) return;

    const billingCountryCode = getCountryCodeFromCountryName(billingCountry);
    const countryCode =
      accountCurrency === 'CAD'
        ? 'CA'
        : billingCountryCode === 'CA'
          ? 'US'
          : billingCountryCode;

    if (countryCode && locale?.localeDetails?.isoCode !== countryCode) {
      try {
        const url = URLS.GET_ADMIN_REGIONS(countryCode);
        const regionRes = await axios.get(url);
        setLocale({
          localeDetails: regionRes.data,
          geoLocated: false,
        });
      } catch (err) {
        console.error('Region info not found for subscription country: ' + err);
      }
    }
  }

  async function getZuoraInfo() {
    setStatus(loadingStatus.GET_BMO_SUB);
    try {
      const res = await axios.get(URLS.ZUORA_INFO, {
        headers: {
          Authorization: `Bearer ${ssoUser.access_token}`,
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
          Expires: 'Sat, 01 Jan 2000 00:00:00 GMT',
        },
      });

      const info = res.data;
      setBmoUser((prevBmoUser) => ({
        ...prevBmoUser,
        zuoraInfo: info,
      }));

      // Set locale from billing country
      if (info.billToContact?.country) {
        await setLocaleFromBillingCountry(
          info.billToContact.country,
          info.basicInfo.currency
        );
      } else {
        // Fallback for old subscriptions without billToContact
        info.basicInfo.currency === 'CAD'
          ? setLocale(canadianLocale)
          : setLocale(unitedStatesLocale);
      }

      getFutureAmendments(info);
      if (!info.basicInfo.defaultPaymentMethod) {
        getPONumber(info.basicInfo.currency);
      }
      setStatus(loadingStatus.DONE);
    } catch (err) {
      console.error('BMO user subscription not found');
      setStatus(loadingStatus.DONE);
    }
  }

  async function getZuoraAccountCurrency() {
    setStatus(loadingStatus.GET_BMO_SUB);
    try {
      const res = await axios.get(URLS.ZUORA_INFO, {
        headers: {
          Authorization: `Bearer ${ssoUser.access_token}`,
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
          Expires: 'Sat, 01 Jan 2000 00:00:00 GMT',
        },
      });
      setBmoUser((prevBmoUser) => ({
        ...prevBmoUser,
        zuoraAccountCurrency: res.data.basicInfo.currency,
      }));
    } catch (err) {
      console.error('Zuora account not found');
    }
    setStatus(loadingStatus.DONE);
  }

  async function getGeoLocation() {
    !hasManualLocaleSelection &&
      (!locale ||
        JSON.stringify(locale) == JSON.stringify(unitedStatesLocale)) &&
      (await axios
        .get(URLS.GET_LOCATION)
        .then((res) => {
          getLocationinfo(res.data);
        })
        .catch((err) => {
          console.error('Location not found.');
        }));
  }

  async function getLocationinfo(location) {
    let url = URLS.GET_ADMIN_REGIONS(location?.isoCode?.toUpperCase());
    await axios
      .get(url)
      .then((res) => {
        setLocale({
          localeDetails: res.data,
          geoLocated: true,
        });
        !window.location.href.includes('?lng') &&
          !hasManualLanguageSelection &&
          getLanguageInfo(res.data);
        res.data.exclusive && navigationWrapper('/partner');
      })
      .catch((err) => {
        console.error('Location Info not found: ' + err);
      });
  }

  async function getLanguageInfo(location) {
    let url =
      process.env.INTERNAL_SERVICE_URL +
      'admin/locales/' +
      location?.defaultLocale;
    await axios
      .get(url)
      .then((res) => {
        setSelectedLanguage(res.data.name);
        i18n.changeLanguage(res.data.code.substring(0, 2));
      })
      .catch((err) => {
        console.error('Location not found.');
      });
  }

  async function getLicenses(ssoId) {
    //* check for License Info
    setStatus(loadingStatus.GET_LICENSES);
    await axios
      .get(
        URLS.GET_LICENSES + '?userSsoId=' + ssoId + '&showInherited=' + true,
        {
          headers: {
            Authorization: `Bearer ${ssoUser.access_token}`,
          },
        }
      )
      .then((res) => {
        const info = res.data;
        setBmoUser((prevBmoUser) => ({
          ...prevBmoUser,
          studioLicenses: info,
        }));
      })
      .catch((err) => {
        console.error('No studio licenses found.');
      });
  }

  async function getFutureAmendments(info) {
    //* check for BMO subscription info
    setStatus(loadingStatus.GET_FUTURE_AMENDMENTS);
    const subNumber = info.subscriptions[0].subscriptionNumber;
    await axios
      .get(
        URLS.FUTURE_AMENDMENTS +
          '?currency=' +
          info.basicInfo.currency +
          '&subscriptionNumber=' +
          subNumber,
        {
          headers: {
            Authorization: `Bearer ${ssoUser.access_token}`,
          },
        }
      )
      .then((res) => {
        const info = res.data;
        setBmoUser((prevBmoUser) => ({
          ...prevBmoUser,
          futureAmendments: info,
        }));
      })
      .catch((err) => {
        console.error('No future amendments found');
      });
  }

  async function getPONumber(selectedCurrency) {
    //* check for BMO subscription info
    setStatus(loadingStatus.GET_BMO_SUB);
    await axios
      .get(URLS.GET_PO + '?currency=' + selectedCurrency, {
        headers: {
          Authorization: `Bearer ${ssoUser.access_token}`,
        },
      })
      .then((res) => {
        const PONumber = res.data;
        setBmoUser((prevBmoUser) => ({
          ...prevBmoUser,
          PONumber,
        }));
      })
      .catch((err) => {
        console.error('BMO user subscription not found');
      });
  }

  async function redirectByGeolocation() {
    // Only redirect from root pages to preserve manual navigation capability
    const isRootPage =
      window.location.pathname === '/' ||
      window.location.pathname === '/index.html';

    // Enterprise guard: Only redirect if not already redirected AND on root page
    // This prevents redirect loops while preserving all geo/language/region functionality
    if (!hasGeoRedirected && isRootPage) {
      // Ensure we only navigate to one or the other of these routes. /partner takes priority.
      if (
        locale?.geoLocated === true &&
        locale?.localeDetails?.exclusive == true
      ) {
        setHasGeoRedirected(true);
        navigationWrapper('/partner');
      } else if (
        !bmoUser &&
        locale?.localeDetails?.defaultToSubscription == false &&
        locale?.localeDetails?.exclusive == false &&
        locale?.geoLocated === true
      ) {
        setHasGeoRedirected(true);
        navigationWrapper('/license');
      }
    }
  }

  useEffect(() => {
    //* if a user exists, get BMO user information
    //* -- else --
    //* check for `null`... ssoUser is set to null when a silent sign in attempt is made but the user is not signed in
    if (ssoUser && ssoUser.access_token) {
      //* only fetch BMO user if it doesn't already exist
      //* -- and --
      //* we should get it
      //* -- and --
      //* we haven't already tried this exact token (prevents re-firing when
      //* the ssoUser ref changes without a new access token)
      const tokenAlreadyTried =
        bmoFetchedForToken.current === ssoUser.access_token;
      if (!bmoUser && shouldGetBmoUser && !tokenAlreadyTried) {
        bmoFetchedForToken.current = ssoUser.access_token;
        getBmoUser();
      } else {
        setStatus(loadingStatus.DONE);
      }
      setShouldGetBmoUser(true);
      if (window.location.href.includes('/downloads')) {
        navigationWrapper('/downloads/' + window.location.hash.toLowerCase());
      }
    } else if (ssoUser === null) {
      bmoFetchedForToken.current = null;
      //* done
      setStatus(loadingStatus.DONE);
      redirectByGeolocation();
    }
    // eslint-disable-next-line
  }, [ssoUser]);

  return (
    <UserContext.Provider
      value={{
        ssoUser,
        bmoUser,
        getBmoUser,
        getZuoraInfo,
        signInSilent,
        signIn,
        signOut,
        signedOutElsewhere,
        dismissSignedOutElsewhere,
        userStatus: status,
        selectedCurrency,
        setSelectedCurrency,
        setBmoUser,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

//* hook to provide context to the app
export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within UserContext provider');
  }

  return context;
}
